import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAdminFindMyPermissions } from "@/hooks/admin/adminAdminsHooks";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import {
  canAccess,
  findFirstAccessiblePath,
  getRouteAccess,
} from "@/libs/routeAccess";

const GuardLoader = () => (
  <div className="flex h-full w-full items-center justify-center py-20">
    <div className="size-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
  </div>
);

/**
 * 라우트 레벨 권한 가드 — 메뉴 숨김과 별개로 URL 직접 입력 진입을 차단한다.
 * 경로별 요구 권한/역할은 사이드바와 동일하게 src/libs/routeAccess.ts에서 공유.
 */
export default function RouteAccessGuard() {
  const { pathname } = useLocation();
  const { adminRole, adminToken, backendRole } = useAdminAuth();
  const { data: myPermissions, isError } = useAdminFindMyPermissions();

  const requirement = getRouteAccess(pathname);

  // 매핑에 없는 경로는 제한 없음
  if (!requirement || (!requirement.permission && !requirement.role)) {
    return <Outlet />;
  }

  // 파트너(매체사) 계정은 admin-api 권한 체계 밖 — 별도 흐름이므로 가드 대상 아님
  if (adminRole === "PARTNER_ADMIN") {
    return <Outlet />;
  }

  // 쿠키(토큰)·권한 목록 로딩 전에는 리다이렉트하지 않고 로딩 유지 (깜빡 리다이렉트 방지)
  const isReady = !!adminToken && (myPermissions !== undefined || isError);
  if (!isReady) {
    return <GuardLoader />;
  }

  const permissions = myPermissions ?? [];
  if (canAccess(requirement, permissions, backendRole)) {
    return <Outlet />;
  }

  return (
    <Navigate to={findFirstAccessiblePath(permissions, backendRole)} replace />
  );
}
